import { faCheckCircle, faChevronCircleRight } from "@fortawesome/pro-solid-svg-icons";
import { AnimatePresence, motion } from "framer-motion";
import { PageProps } from "gatsby";
import React, { useState } from "react";
import { Bullet, Counter, DomScale, Slide, Thumb } from "..";
import { Locale } from "../../../config";
import { Button } from "../../shared/Button";
import { IPhone, Ipad } from "../../shared/IPad";
import { Img, Svg, useScreenDimensions } from "../../shared/Img";
import { Loc, LocaleLanguage, imageUrlSwitch, locPath, makeI18n } from "../../shared/Languages";
import { LinkAsButton } from "../../shared/LinkAsButton";
import { Navbar } from "../../shared/Navbar";
import { Section } from "../../shared/Section";
import { SectionVideoBackdrop } from "../../shared/SectionVideoBackdrop";
import { useSlideshow } from "../../shared/SolarAnimation";
import { SolarReferences } from "../../shared/SolarReferences";
import { TabButton, TabToggleContainer } from "../../shared/TabButton";
import { useNewsletter } from "../../shared/newsletter/newsletter";
import { Footer } from "../Footer";
import { NewSupportedExpansionLocale } from "../landing_page_expansion";

export default function Index({
    locale,
    ...props
}: PageProps & {
    locale: Exclude<Locale, NewSupportedExpansionLocale | "DE-DE" | "DE-AT" | "DE-CH"> | "NL-NL" | "FR-CA" | "EN-CA";
}) {
    const language = LocaleLanguage[locale];

    const [showSlide, setShowSlide] = useState<number | null>(0);

    const slideshow = useSlideshow({
        interval: 3000,
        nrOfSlides: 4,
    });

    const { subscribe, loading: newsletterLoading } = useNewsletter({
        locale: locale,
        type: "general newsletter",
    });

    const [email, setEmail] = useState<string>();
    const [name, setName] = useState<string>();
    const [subscribed, setSubscribed] = useState(false);
    const [newsletterError, setNewsletterError] = useState<boolean>(false);

    const [featuresView, setFeaturesView] = useState<"1" | "2">("2");

    const { i18n } = makeI18n(language);
    const mobile = useScreenDimensions(640);

    return (
        <>
            <Navbar locale={locale} />
            <Section relative={false} id="top-hover">
                <div className="w-full grid grid-cols-1 grid-rows-1">
                    <Slide
                        nr={0}
                        show={showSlide === null ? null : showSlide === 0}
                        delay={0 * 1000}
                        twColor="bg-reonic-green"
                        initial
                    >
                        <div className="flex flex-col text-white text-sm sm:text-base">
                            <h1 className="left-0 top-0 text-3xl xs:text-3xl md:text-5xl lg:text-5xl font-bold mb-2 md:mb-5 leading-[36px] xs:leading-[36px] md:leading-[56px] lg:leading-[58px]">
                                {i18n({
                                    EN: "Master the energy transition.",
                                    SV: "Bemästra energiomställningen.",
                                    PTBR: "Domine a transição energética.",
                                    PT: "Domine a transição energética.",
                                    PL: "Opanuj transformację energetyczną.",
                                    ES: "Tu software para dominar la transición energética.",
                                    CZ: "Váš software pro zvládnutí energetického přechodu.",
                                    FR: "Votre logiciel pour maîtriser la transition énergétique.",
                                    IT: "Il tuo software per gestire la transizione energetica",
                                    RO: "Perfecționează tranziția energetică.",
                                    NL: "Beheers de energietransitie.",
                                })}
                            </h1>
                            <div className="">
                                {i18n({
                                    EN: "For more speed in your sales process. So you and your team can work faster.",
                                    SV: "För mer fart i din säljprocess. Så att du och ditt team kan arbeta snabbare.",
                                    PTBR: "Para aumentar a velocidade do seu processo de vendas. Para que você e sua equipe possam trabalhar de forma mais rápida e inteligente",
                                    PT: "Para uma maior celeridade no seu processo de vendas. Para que a sua equipa possa trabalhar de forma mais rápida e inteligente.",
                                    PL: "Aby przyspieszyć proces sprzedaży. Ty i Twój zespół możecie pracować sprawniej i efektywniej.",
                                    ES: "Agiliza tu proceso de ventas para que tú y tu equipo, podáis trabajar más rápido.",
                                    CZ: "Pro větší rychlost prodejního procesu. Vy a váš tým tak můžete pracovat rychleji.",
                                    FR: "Un processus de vente plus rapide. Vous et votre équipe pouvez ainsi travailler plus efficacement.",
                                    IT: "Velocizza e migliora il processo di vendita, così tu e il tuo team potrete lavorare più efficacemente.",
                                    RO: "Pentru mai multă viteză în procesul de vânzări. Pentru ca tu și echipa ta să puteți lucra mai repede.",
                                    NL: "Voor meer snelheid in uw verkoopproces. Zodat u en uw team sneller kunnen werken.",
                                })}
                            </div>{" "}
                            <LinkAsButton
                                to={locPath(locale, "/book-a-meeting/")}
                                className="mt-4 bg-black text-white whitespace-nowrap"
                            >
                                {i18n({
                                    EN: "Get a demo",
                                    SV: "Boka en demo",
                                    PTBR: "Agendar uma demonstração",
                                    PT: "Pedir demonstração",
                                    PL: "Umów demo",
                                    ES: "Solicite una demo",
                                    CZ: "Získejte ukázku",
                                    FR: "Demander une démo",
                                    IT: "Richiedi una demo",
                                    RO: "Obțineți un demonstrație",
                                    NL: "Vraag een demo aan",
                                })}
                            </LinkAsButton>
                        </div>
                        <div className="relative flex justify-start items-start">
                            <div className="hidden sm:block">
                                <Ipad shadow landscape className="bottom-[20px] left-[20px]">
                                    <Img
                                        size="md"
                                        noPreload
                                        alt=""
                                        className="h-full w-full object-contain"
                                        src={imageUrlSwitch(language, "/pages/_assets/energiehaus blau hero en.png", {
                                            FR: "/pages/_assets/fr/index_energyhouse_fr.png",
                                        })}
                                    />
                                </Ipad>
                                <IPhone
                                    shadow
                                    whitebar
                                    className="absolute -left-[20px] -bottom-[20px]"
                                    innerClassName=""
                                >
                                    <div className="w-full h-full overflow-hidden">
                                        <Img
                                            noPreload
                                            size="sm"
                                            className="h-[270px] bg-white w-full object-contain"
                                            src={imageUrlSwitch(language, "/pages/_assets/Phone en.png", {
                                                FR: "/pages/_assets/fr/index_phone_fr.png",
                                            })}
                                            alt="0"
                                        />
                                    </div>
                                </IPhone>
                            </div>
                            <DomScale scale={50} className="sm:hidden">
                                <Ipad shadow landscape className="bottom-[20px] left-[20px]">
                                    <Img
                                        noPreload
                                        size="md"
                                        alt=""
                                        className="h-full w-full object-contain"
                                        src={imageUrlSwitch(language, "/pages/_assets/energiehaus blau hero en.png", {
                                            FR: "/pages/_assets/fr/index_energyhouse_fr.png",
                                        })}
                                    />
                                </Ipad>
                                <IPhone
                                    shadow
                                    whitebar
                                    className="absolute md:-left-[20px] bottom-0 md:-bottom-[20px]"
                                    innerClassName=""
                                >
                                    <div className="w-full h-full overflow-hidden">
                                        <Img
                                            noPreload
                                            size="md"
                                            sizeMobile="sm"
                                            className="bg-white w-full"
                                            src={imageUrlSwitch(language, "/pages/_assets/Phone en.png", {
                                                FR: "/pages/_assets/fr/index_phone_fr.png",
                                            })}
                                            alt="0"
                                        />
                                    </div>
                                </IPhone>
                            </DomScale>
                        </div>
                    </Slide>
                </div>
                {/* {!mobile && (
                    <div className="w-full justify-center gap-2 mt-10 md:mt-14 hidden sm:flex">
                        <Thumb
                            twColor="bg-reonic-green"
                            onClick={() => showSlideImm !== 0 && showSlideManually(0)}
                            selected={showSlideImm === 0}
                        />
                        <Thumb
                            twColor="bg-reonic-blue"
                            onClick={() => showSlideImm !== 1 && showSlideManually(1)}
                            selected={showSlideImm === 1}
                        />
                        <Thumb
                            twColor="bg-reonic-red"
                            onClick={() => showSlideImm !== 2 && showSlideManually(2)}
                            selected={showSlideImm === 2}
                        />
                    </div>
                )} */}
                <div className="col-span-full mt-14 mb-20 flex flex-col items-center">
                    <SolarReferences locale={locale} />

                    <div className="w-full flex items-center whitespace-nowrap justify-center gap-3 text-sm font-medium text-black mt-4">
                        <hr className="w-[30px] border-zinc-200" />
                        {i18n({
                            EN: "In use every day with 1000+ customers",
                            SV: "Används varje dag av över 1000 kunder",
                            PTBR: "Utilizado diariamente por mais de 1000 clientes",
                            PT: "Utilizado diariamente por mais de 1000 clientes",
                            PL: "Używany codziennie przez ponad 1000 klientów",
                            ES: "Se utiliza a diario con más de 1000 clientes",
                            CZ: "Používá se každý den u více než 1000 zákazníků",
                            FR: "Plus de 1000 clients nous font déjà confiance.",
                            IT: "In uso ogni giorno con oltre 1000 clienti",
                            RO: "Utilizat în fiecare zi de peste 1000 de clienți",
                            NL: "Dagelijks in gebruik bij meer dan 1000 klanten",
                        })}
                        <hr className="w-[30px] border-zinc-200" />
                    </div>
                </div>

                <div className="md:pt-64 pt-32 pb-32 relative">
                    <div className="absolute w-full h-full left-0 top-[50px] flex flex-col items-center justify-start -z-10">
                        <div className="rotate-[-5deg] shrink-0 w-[10000px] h-[1000px] bg-zinc-100" />
                    </div>
                    <div className="flex gap-3 pb-4">
                        <Svg src="/shared/icon_solar.svg" alt="elements" className="h-16 bg-white rounded-lg p-0.5" />
                        <Svg
                            src="/shared/icon_energiespeicher.svg"
                            alt="elements"
                            className="h-16 bg-white rounded-lg p-0.5"
                        />
                        <Svg
                            src="/shared/icon_waermepumpe.svg"
                            alt="elements"
                            className="h-16 bg-white rounded-lg p-0.5"
                        />
                        <Svg src="/shared/icon_wallbox.svg" alt="elements" className="h-16 bg-white rounded-lg p-0.5" />
                    </div>
                    <div className="font-bold 2xl:text-5xl md:text-4xl text-3xl leading-[1.1]">
                        {i18n({
                            EN: "Simply the best tool for selling and installing photovoltaics, energy storage systems, wallboxes and heat pumps.",
                            SV: "Helt enkelt det bästa verktyget för att sälja och installera solceller, energilagringssystem, wallboxar och värmepumpar.",
                            PTBR: "Simplesmente a melhor ferramenta para vender e instalar sistemas fotovoltaicos, sistemas de armazenamento de energia, wallboxes e bombas de calor.",
                            PT: "Simplesmente a melhor ferramenta para a venda e instalação de sistemas fotovoltaicos, baterias, wallboxes e bombas de calor.",
                            PL: "Najlepsze rozwiązanie do sprzedaży i instalacji fotowoltaiki, magazynów energii, wallboxów i pomp ciepła.",
                            ES: "Sencillamente, la mejor herramienta para vender e instalar sistemas fotovoltaicos, sistemas de almacenamiento de energía, wallboxes y bombas de calor.",
                            CZ: "Jednoduše nejlepší nástroj pro prodej a instalaci fotovoltaiky, systémů skladování energie, wallboxů a tepelných čerpadel.",
                            FR: "Le meilleur outil pour vendre et installer des systèmes photovoltaïques, des systèmes de stockage d'énergie, des bornes de recharge murales ainsi que des pompes à chaleur.",
                            IT: "Il miglior strumento per la vendita e l'installazione di impianti fotovoltaici, sistemi di accumulo di energia, wallbox e pompe di calore.",
                            RO: "Pur și simplu cel mai bun instrument pentru vânzarea și instalarea de sisteme fotovoltaice, sisteme de stocare a energiei, stații de încărcare de perete și pompe de căldură.",
                            NL: "Simpelweg het beste hulpmiddel voor het verkopen en installeren van zonnepanelen, energieopslagsystemen, wallboxen en warmtepompen.",
                        })}
                    </div>

                    <div className="font-medium text-zinc-500 md:text-2xl text-xl mt-3">
                        <Loc
                            language={language}
                            EN={
                                <>
                                    Our software as your reliable partner for growth.
                                    <br />
                                    Ideal for both private and commercial clients, we've got every step covered.
                                    <br />
                                    From A to Z, from interest to installation.
                                </>
                            }
                            SV={
                                <>
                                    Vår programvara är din pålitliga partner för tillväxt.
                                    <br />
                                    Vi ör det idealiska valet för både privata och kommersiella kunder och vi tar hand
                                    om varje steg.
                                    <br />
                                    Från A till Ö, från intresse till installation
                                </>
                            }
                            PTBR={
                                <>
                                    Nosso software é seu parceiro confiável para o crescimento.
                                    <br />
                                    Ideal para residenciais privados e comerciais, temos todas as etapas cobertas.
                                    <br />
                                    De A a Z, do interesse à instalação.
                                </>
                            }
                            PT={
                                <>
                                    Encontre no nosso software um parceiro de confiança.
                                    <br />
                                    Ideal para clientes residenciais e comerciais, temos todos os processos cobertos.
                                    <br />
                                    De A a Z, do interesse inicial à instalação.
                                </>
                            }
                            PL={
                                <>
                                    Nasze oprogramowanie jako niezawodny partner w rozwoju.
                                    <br />
                                    Jesteśmy idealnym rozwiązaniem zarówno dla klientów prywatnych, jak i komercyjnych.
                                    <br />
                                    Od A do Z: od zainteresowania do instalacji.
                                </>
                            } //
                            ES={
                                <>
                                    Nuestro software es el socio de confianza para tu crecimiento.
                                    <br />
                                    Ideal tanto para clientes privados como comerciales, cubrimos todos los pasos.
                                    <br />
                                    De la A a la Z, del interés a la instalación.
                                </>
                            }
                            CZ={
                                <>
                                    Náš software jako váš spolehlivý partner pro růst.
                                    <br />
                                    Jsme ideální pro soukromé i komerční klienty a máme pro ně zajištěn každý krok.
                                    <br />
                                    Od A do Z, od zájmu po instalaci.
                                </>
                            }
                            FR={
                                <>
                                    Notre logiciel est le partenaire le plus fiable pour votre croissance. <br />
                                    Il est idéal pour les clients privés comme les commerciaux, nous avons tout prévu.
                                    <br />
                                    De A à Z, de la curiosité à l'installation.{" "}
                                </>
                            }
                            IT={
                                <>
                                    Il nostro software è il partner affidabile per la tua crescita.
                                    <br />
                                    Progettato per soddisfare le esigenze di clienti privati e commerciali, copre tutte
                                    le fasi del processo di vendita.
                                    <br />
                                    Dalla A alla Z, dall'interesse iniziale all'installazione.
                                </>
                            }
                            RO={
                                <>
                                    Software-ul nostru ca partener de încredere pentru creștere.
                                    <br />
                                    Ideal atât pentru clienții privați, cât și pentru cei comerciali, am acoperit
                                    fiecare pas.
                                    <br />
                                    De la A la Z, de la interes la instalare.
                                </>
                            }
                            NL={
                                <>
                                    Onze software als uw betrouwbare partner voor groei.
                                    <br />
                                    Ideaal voor zowel particuliere als zakelijke klanten, we hebben elke stap gedekt.
                                    <br />
                                    Van A tot Z, van interesse tot installatie.
                                </>
                            }
                        />
                    </div>
                </div>
            </Section>

            <Section className="pb-64" outerClassName="bg-zinc-100">
                {/* <div className="mb-16">
                    <h1 className="font-bold text-3xl sm:text-4xl md:text-5xl">
                        Software von A bis Z. Ganzheitlich gedacht.
                    </h1>
                    <h2 className="font-medium text-zinc-500 text-3xl sm:text-4xl md:text-2xl mt-[4px]">
                        Für alle Prozesschritte von Kundeninteresse bis Installation.
                    </h2>
                </div> */}
                <div className="flex gap-2 items-end">
                    <IPhone
                        whitebar
                        className="z-20 shrink-0 shadow-2xl "
                        children={
                            <div className="w-full h-full overflow-hidden">
                                <Img
                                    size="sm"
                                    className="h-[270px] bg-white w-full object-contain"
                                    src={imageUrlSwitch(language, "/pages/_assets/Phone en.png", {
                                        FR: "/pages/_assets/fr/index_phone_fr.png",
                                    })}
                                    alt="0"
                                />
                            </div>
                        }
                    />
                    <Ipad
                        className="-ml-96 md:-ml-20 z-10 shrink-0 shadow-2xl hidden md:block"
                        landscape
                        children={
                            <Img
                                size="md"
                                className="object-contain w-full h-full"
                                src={imageUrlSwitch(language, "/pages/_assets/Detailplanung en.png", {
                                    FR: "/pages/_assets/fr/index_roof_top.png",
                                })}
                                alt=""
                            />
                        }
                    />
                    <DomScale className="md:hidden block" scale={70}>
                        <Ipad
                            className="-ml-32 -z-20 md:z-50 shrink-0 shadow-2xl "
                            landscape
                            children={
                                <Img
                                    size="lg"
                                    sizeMobile="md"
                                    className="object-contain w-full h-full"
                                    src={imageUrlSwitch(language, "/pages/_assets/Detailplanung en.png", {
                                        FR: "/pages/_assets/fr/index_roof_top.png",
                                    })}
                                    alt=""
                                />
                            }
                        />
                    </DomScale>
                    <div className="shrink-0 -ml-[450px] md:-ml-[380px] md:block hidden">
                        <div className="bg-zinc-100 px-[1px] pt-[1px] rounded-t-3xl">
                            <div className="px-2.5 pt-2.5 pb-5 bg-black rounded-t-[23px] relative">
                                <div className="absolute left-0 top-0 w-full flex justify-center">
                                    <div className="w-20 h-6 bg-black z-1 rounded-b-md" />
                                </div>
                                <div className="overflow-hidden bg-white rounded-t-[16px]">
                                    <div className="h-4 bg-zinc-50 w-full" />
                                    <div className=" h-[500px] w-[800px] overflow-hidden px-2 pt-1">
                                        <Img
                                            size="lg"
                                            sizeMobile="md"
                                            className="object-cover w-full "
                                            src={imageUrlSwitch(language, "/pages/de-de/industry/solar/kanban en.png", {
                                                FR: "/pages/_assets/fr/index_kanban_fr.png",
                                            })}
                                            alt=""
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="bg-zinc-100 rounded-b-xl shadow-lg relative">
                            <div className="h-[1px] relative w-full">
                                <div className="absolute left-0 top-0 bg-gradient-to-b from-zinc-200/50 to-zinc-500/50 h-full" />
                                <div className="absolute left-0 top-0 bg-gradient-to-r from-zinc-700 to-zinc-900 h-full" />
                            </div>
                            <div className="relative h-7 rounded-b-[13px] overflow-hidden">
                                <div className="absolute left-0 top-0 w-full h-full bg-gradient-to-r from-zinc-400/50 to-zinc-400/50" />
                                <div className="absolute right-0 top-0 w-[200px] h-[200px] bg-gradient-to-r from-transparent to-zinc-400/75 rotate-[25deg] -mt-[125px] -mr-[20px]" />
                                <div className="absolute -right-2 top-0 w-[50px] h-[50px] bg-gradient-to-r from-transparent via-zinc-400 to-transparent" />
                            </div>
                            <div className="absolute left-0 top-0 w-full flex justify-center z-1">
                                <div className="relative w-36 h-3 rounded-b-[20px] overflow-hidden">
                                    <div className="absolute left-0 top-0 w-full h-full bg-gradient-to-b from-zinc-200/50 to-zinc-200/50" />
                                    <div className="absolute left-0 top-0 w-full h-full bg-gradient-to-r from-zinc-300/50 to-zinc-50/75" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="mt-24" />
                <TabToggleContainer twBackground="bg-zinc-200">
                    <TabButton //
                        active={featuresView === "2"}
                        onClick={() => setFeaturesView("2")}
                        twActive="bg-reonic-yellow text-white"
                    >
                        {i18n({
                            EN: "Products",
                            SV: "Produkter",
                            PTBR: "Produtos",
                            PT: "Produtos",
                            PL: "Produkty",
                            ES: "Productos",
                            CZ: "Produkty",
                            FR: "Produits",
                            IT: "Prodotti",
                            RO: "Produse",
                            NL: "Producten",
                        })}
                    </TabButton>
                    <TabButton //
                        active={featuresView === "1"}
                        onClick={() => setFeaturesView("1")}
                        twActive="bg-reonic-green text-white"
                    >
                        {i18n({
                            EN: "Use-Cases",
                            SV: "Användningsfall",
                            PTBR: "Casos de uso",
                            PT: "Casos de utilização",
                            PL: "Zastosowania",
                            ES: "Casos prácticos",
                            CZ: "Případy použití",
                            FR: "Cas d'utilisation",
                            IT: "Casi d'uso",
                            RO: "Cazuri de utilizare",
                            NL: "Usecases",
                        })}
                    </TabButton>
                </TabToggleContainer>

                <AnimatePresence>
                    <div className="bg-white shadow-2xl px-8 py-6 -mx-3.5 relative mt-5 rounded-lg text-zinc-500 font-semibold text-lg">
                        <div
                            className={`
                                h-8 w-8 overflow-hidden rotate-45 -mt-7
                                transition-all
                                ${featuresView === "2" && "sm:ml-[20px] ml-[10px]"}
                                ${featuresView === "1" && "sm:ml-[130px] ml-[90px]"}
                            `}
                        >
                            <div className="-ml-4 -mt-4 w-8 h-8 bg-white rotate-45" />
                        </div>
                        <AnimatePresence>
                            <div className="grid grid-cols-1">
                                {featuresView === "2" && (
                                    <motion.div
                                        key="2"
                                        initial={{ opacity: 0.5 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0.8 }}
                                        transition={{
                                            duration: 0.3,
                                            delay: featuresView === "2" ? 0.1 : 0,
                                        }}
                                        className=""
                                    >
                                        <div className="grid md:grid-cols-[1fr_50px_1fr] gap-4 md:gap-0">
                                            {/*  */}
                                            <div className="flex flex-col border-zinc-100 gap-2">
                                                <div className="bg-zinc-100 rounded-lg flex items-center justify-center p-0.5 shrink-0 h-11 w-11">
                                                    <Svg
                                                        alt="360h-icon"
                                                        className="h-full w-full"
                                                        src="/shared/icon_energiehaus.svg"
                                                    />
                                                </div>
                                                <div className="-mt-1 h-16">
                                                    <div className="mb-1.5 font-semibold text-lg text-black">
                                                        {i18n({
                                                            EN: "360° Household",
                                                            SV: "360° Hushåll",
                                                            PTBR: "Casa 360°",
                                                            PT: "360° Casa",
                                                            PL: "360- Dla domu",
                                                            ES: "Hogar360",
                                                            CZ: "360° Household",
                                                            FR: "360° Household",
                                                            IT: "360° Household",
                                                            RO: "Gospodărie 360°",
                                                            NL: "360° huishouden",
                                                        })}
                                                    </div>
                                                    <div className="text-xs text-zinc-500 font-normal -mt-2 mb-3">
                                                        {i18n({
                                                            EN: "Our all-in-one solution for selling to private customers.",
                                                            SV: "Vår allt-i-ett-lösning för försäljning till privatkunder.",
                                                            PTBR: "Nossa solução completa para vendas a clientes particulares.",
                                                            PT: "A nossa solução tudo-em-um para vender a clientes particulares.",
                                                            PL: "Kompleksowe rozwiązanie do sprzedaży klientom prywatnym.",
                                                            ES: "Nuestras soluciones 'todo en uno' para vender a particulares.",
                                                            CZ: "Naše komplexní řešení pro prodej soukromým zákazníkům.",
                                                            FR: "Nos solutions tout-en-un pour la vente aux particuliers.",
                                                            IT: "Le nostre soluzioni all-in-one da vendere ai clienti privati.",
                                                            RO: "Soluția noastră all-in-one pentru vânzarea către clienți privați.",
                                                            NL: "Onze alles-in-één oplossing voor verkoop aan particuliere klanten.",
                                                        })}
                                                    </div>
                                                </div>

                                                <Bullet
                                                    icon={faCheckCircle}
                                                    title={i18n({
                                                        EN: "Generate qualified leads",
                                                        SV: "Generera kvalificerade leads",
                                                        PTBR: "Gerar leads qualificados",
                                                        PT: "Geração de leads qualificadas",
                                                        PL: "Generowanie i kwalifikowanie potencjalnych klientów",
                                                        ES: "Califica a nuevos clientes",
                                                        CZ: "Kvalifikace generování potenciálních zákazníků",
                                                        FR: "Génération de prospects qualifiés",
                                                        IT: "Genera nuovi lead e contatti qualificati",
                                                        RO: "Generați clienți potențiali calificați",
                                                        NL: "Genereer gekwalificeerde leads",
                                                    })}
                                                />
                                                <Bullet
                                                    icon={faCheckCircle}
                                                    title={i18n({
                                                        EN: "Detailed & quickplan mode",
                                                        SV: "Detaljerat och snabbt planeringsläge",
                                                        PTBR: "Modo de planejamento detalhado e rápido",
                                                        PT: "Modo de planeamento rápido, e detalhado",
                                                        PL: "Tryb szczegółowego i szybkiego planowania",
                                                        ES: "Modos de planes detallados y rápidos",
                                                        CZ: "Režimy podrobného a rychlého plánu",
                                                        FR: "2 modes : plan détaillé et plan rapide",
                                                        IT: "Modalità di pianificazione dettagliata e rapida",
                                                        RO: "Mod plan detaliat și rapid",
                                                        NL: "Gedetailleerde- & snelplanningsmodus",
                                                    })}
                                                />
                                                <Bullet
                                                    icon={faCheckCircle}
                                                    title={i18n({
                                                        EN: "Simulate energy flows",
                                                        SV: "Simulera energiflöden",
                                                        PTBR: "Simular fluxos de energia",
                                                        PT: "Simulação de fluxos de energia",
                                                        PL: "Symulacja przepływów energii",
                                                        ES: "Simular flujos de energía",
                                                        CZ: "Simulace toků energie",
                                                        FR: "Simulation des flux énergétiques",
                                                        IT: "Simulare i flussi di energia",
                                                        RO: "Simularea fluxurilor de energie",
                                                        NL: "Simuleer energiestromen",
                                                    })}
                                                />
                                                <Bullet
                                                    icon={faCheckCircle}
                                                    title={i18n({
                                                        EN: "Profitability calculation",
                                                        SV: "Lönsamhetskalkyl",
                                                        PTBR: "Cálculo de rentabilidade",
                                                        PT: "Cálculo da rendibilidade",
                                                        PL: "Obliczanie rentowności",
                                                        ES: "Cálculo de la rentabilidad",
                                                        CZ: "Výpočet ziskovosti",
                                                        FR: "Calcul de la rentabilité",
                                                        IT: "Calcolo della redditività",
                                                        RO: "Calculul profitabilității",
                                                        NL: "Rentabiliteitsberekening",
                                                    })}
                                                />
                                                <Bullet
                                                    icon={faCheckCircle}
                                                    title={i18n({
                                                        EN: "Easy preparation of quotations",
                                                        SV: "Enkel beredning av offerter",
                                                        PTBR: "Fácil preparação de cotações",
                                                        PT: "Preparação fácil de orçamentos",
                                                        PL: "Proste przygotowywanie ofert",
                                                        ES: "Preparación sencilla de presupuestos",
                                                        CZ: "Jednoduchá příprava nabídek",
                                                        FR: "Préparation simple des devis",
                                                        IT: "Preparazione semplice dei preventivi",
                                                        RO: "Pregătirea ușoară a ofertelor de preț",
                                                        NL: "Eenvoudige voorbereiding van offertes",
                                                    })}
                                                />
                                                <Bullet
                                                    icon={faCheckCircle}
                                                    title={i18n({
                                                        EN: "Automatic PV-layout",
                                                        SV: "Automatisk PV-layout",
                                                        PTBR: "Layout automático dos painéis solares",
                                                        PT: "Disposição automática de PV",
                                                        PL: "Automatyczne planowanie pokrycia dachu",
                                                        ES: "Techos fotovoltaicos automáticos",
                                                        CZ: "Automatické fotovoltaické zastřešení",
                                                        FR: "Planification automatique de toiture photovoltaïque",
                                                        IT: "Tetto fotovoltaico automatico",
                                                        RO: "Layout PV automat",
                                                        NL: "Automatische PV-layout",
                                                    })}
                                                />
                                                <div className="md:pt-4" />
                                                {/* do not link to pages that not yet exist */}
                                                {/* <LinkAsButton
                                                    to={locPath(locale, "/product/360h/")}
                                                    className="bg-zinc-100 hover:bg-zinc-200 text-zinc-700 hover:text-black w-fit mt-auto ml-auto"
                                                >
                                                    <Loc
                                                        language={language}
                                                        EN={<>Learn more</>}
                                                        PL={<>Dowiedz się więcej</>}
                                                        ES={<>Más información</>}
                                                        CZ={<>Zjistěte více</>}
                                                        FR={<>En savoir plus</>}
                                                        IT={<>Per saperne di più</>}
                                                    />
                                                </LinkAsButton> */}
                                            </div>
                                            <div className="flex justify-center h-full">
                                                <div className="border-l h-full shrink-0" />
                                            </div>
                                            <div className="flex flex-col border-zinc-100 gap-2">
                                                <div className="bg-zinc-100 rounded-lg flex items-center justify-center p-0.5 shrink-0 h-11 w-11">
                                                    <Svg
                                                        alt="360b-icon"
                                                        className="h-full w-full"
                                                        src="/shared/icon_energiefirma.svg"
                                                    />
                                                </div>
                                                <div className="-mt-1 h-16 flex flex-col">
                                                    <div className="mb-1.5 font-semibold text-lg text-black">
                                                        {i18n({
                                                            EN: "360° Commercial",
                                                            SV: "360° Kommersiell",
                                                            PTBR: "Comercial 360°",
                                                            PT: "360° Empresa",
                                                            PL: "360- Dla firmy",
                                                            ES: "Comercial360",
                                                            CZ: "360° Commercial",
                                                            FR: "360° Commercial",
                                                            IT: "360° Commercial",
                                                            RO: "360° Comercial",
                                                            NL: "360° commercieel",
                                                        })}
                                                    </div>
                                                    <div className="text-xs text-zinc-500 font-normal -mt-2 mb-3">
                                                        {i18n({
                                                            EN: "Our all-in-one solution for selling to commercial customers.",
                                                            SV: "Vår allt-i-ett-lösning för försäljning till kommersiella kunder.",
                                                            PTBR: "Nossa solução completa para vendas a clientes comerciais.",
                                                            PT: "A nossa solução tudo-em-um para vender a clientes comerciais.",
                                                            PL: "Nasze kompleksowe rozwiązanie do sprzedaży klientom komercyjnym.",
                                                            ES: "Nuestra solución todo en uno para vender a clientes comerciales.",
                                                            CZ: "Naše komplexní řešení pro prodej komerčním zákazníkům.",
                                                            FR: "Notre solution tout-en-un pour vendre aux clients commerciaux.",
                                                            IT: "La nostra soluzione all-in-one per la vendita ai clienti commerciali.",
                                                            RO: "Soluția noastră all-in-one pentru vânzarea către clienți comerciali.",
                                                            NL: "Onze alles-in-één oplossing voor verkoop aan zakelijke klanten.",
                                                        })}
                                                    </div>
                                                </div>

                                                <Bullet
                                                    icon={faCheckCircle}
                                                    title={i18n({
                                                        EN: "Detailed & quickplan mode",
                                                        SV: "Detaljerat och snabbt planeringsläge",
                                                        PTBR: "Modo de planejamento detalhado e rápido",
                                                        PT: "Modo de planeamento rápido, e detalhado",
                                                        PL: "Tryb szczegółowego i szybkiego planowania",
                                                        ES: "Modos de planes detallados y rápidos",
                                                        CZ: "Režim podrobného a rychlého plánu",
                                                        FR: "Mode plan détaillé et mode plan rapide",
                                                        IT: "Modalità dettagliata e piano rapido",
                                                        RO: "Mod plan detaliat și rapid",
                                                        NL: "Gedetailleerde- & snelplanningsmodus",
                                                    })}
                                                />

                                                <Bullet
                                                    icon={faCheckCircle}
                                                    title={i18n({
                                                        EN: "Include grid connections & calculate grid charges",
                                                        SV: "Inkludera nätanslutningar och beräkna nätavgifter",
                                                        PTBR: "Incluir conexões de rede e calcular encargos de rede",
                                                        PT: "Consideração de ligações à rede no cálculo dos custos energéticos",
                                                        PL: "Uwzględnienie połączeń sieciowych i obliczanie opłat",
                                                        ES: "Incluir las conexiones a la red y calcular las tarifas de red",
                                                        CZ: "Zahrnutí připojení k síti a výpočet poplatků za připojení k síti",
                                                        FR: "Prise en compte des connexions au réseau et calcul des frais associés",
                                                        IT: "Includere le connessioni alla rete e calcolare gli oneri di rete",
                                                        RO: "Includerea conexiunilor la rețea și calcularea tarifelor de rețea",
                                                        NL: "Inclusief netaansluitingen & berekening van netkosten",
                                                    })}
                                                />

                                                <Bullet
                                                    icon={faCheckCircle}
                                                    title={i18n({
                                                        EN: "In-depth profitability calculation",
                                                        SV: "Fördjupad lönsamhetskalkyl",
                                                        PTBR: "Cálculo detalhado da lucratividade",
                                                        PT: "Cálculo aprofundado da rentabilidade económica",
                                                        PL: "Szczegółowa kalkulacja rentowności",
                                                        ES: "Cálculo exhaustivo de la rentabilidad",
                                                        CZ: "Hloubkový výpočet ziskovosti",
                                                        FR: "Calcul de rentabilité approfondi",
                                                        IT: "Calcolo approfondito della redditività",
                                                        RO: "Calcul aprofundat al rentabilității",
                                                        NL: "Diepgaande rentabiliteitsberekening",
                                                    })}
                                                />

                                                <Bullet
                                                    icon={faCheckCircle}
                                                    title={i18n({
                                                        EN: "Plan complex systems with multiple energy generators and consumers",
                                                        SV: "Planera komplexa system med flera energiproducenter och -konsumenter",
                                                        PTBR: "Planeje sistemas complexos com vários geradores e consumidores de energia",
                                                        PT: "Planeamento de sistemas complexos com múltiplos produtores e consumidores de energia",
                                                        PL: "Planowanie złożonych systemów z wieloma generatorami i odbiornikami energii",
                                                        ES: "Mitme energiatootja ja -tarbijaga keerukate süsteemide planeerimine",
                                                        CZ: "Plánování složitých systémů s více generátory a spotřebiči energie.",
                                                        FR: "Planification de systèmes complexes avec de multiples générateurs et consommateurs d'énergie",
                                                        IT: "Pianificare sistemi complessi con più generatori e consumatori di energia.",
                                                        RO: "Planificarea sistemelor complexe cu generatori și consumatori multipli de energie",
                                                        NL: "Plan complexe systemen met meerdere energieopwekkers en -verbruikers",
                                                    })}
                                                />
                                                <Bullet
                                                    icon={faCheckCircle}
                                                    title={i18n({
                                                        EN: "Peak-Shaving",
                                                        SV: "Peak-Shaving",
                                                        PTBR: "Redução de picos",
                                                        PT: "Peak-Shaving",
                                                        PL: "Peak-Shaving",
                                                        ES: "Reducción de picos",
                                                        CZ: "Peak-Shaving",
                                                        FR: "Peak-Shaving",
                                                        IT: "Peak-Shaving",
                                                        RO: "Reducerea vârfurilor de consum",
                                                        NL: "Peak shaving",
                                                    })}
                                                />
                                                <Bullet
                                                    icon={faCheckCircle}
                                                    title={i18n({
                                                        EN: "Optimization of energy storage systems",
                                                        SV: "Optimering av energilagringssystem",
                                                        PTBR: "Otimização de sistemas de armazenamento de energia",
                                                        PT: "Otimização de sistemas de armazenamento de energia",
                                                        PL: "Optymalizacja systemów magazynowania energii",
                                                        ES: "Optimización de los sistemas de almacenamiento de energía",
                                                        CZ: "Optimalizace systémů skladování energie",
                                                        FR: "Optimisation des systèmes de stockage d'énergie",
                                                        IT: "Ottimizzazione dei sistemi di accumulo di energia",
                                                        RO: "Optimizarea sistemelor de stocare a energiei",
                                                        NL: "Optimalisatie van energieopslagsystemen",
                                                    })}
                                                />

                                                <div className="md:pt-4" />
                                                {/* <LinkAsButton
                                                    to={locPath(locale, "/product/360b/")}
                                                    className="bg-zinc-100 hover:bg-zinc-200 text-zinc-700 hover:text-black w-fit mt-auto ml-auto"
                                                >
                                                    <Loc
                                                        language={language}
                                                        EN={<>Learn more</>}
                                                        PL={<>Dowiedz się więcej</>}
                                                        ES={<>Más información</>}
                                                        CZ={<>Zjistěte více</>}
                                                        FR={<>En savoir plus</>}
                                                        IT={<>Per saperne di più</>}
                                                    />
                                                </LinkAsButton> */}
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                                {featuresView === "1" && (
                                    <motion.div
                                        key="1"
                                        initial={{ opacity: 0.5 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0.8 }}
                                        transition={{
                                            duration: 0.3,
                                            delay: featuresView === "1" ? 0.1 : 0,
                                        }}
                                        className=""
                                    >
                                        <div className="grid md:grid-cols-[1fr_50px_1fr_50px_1fr]">
                                            <div className="flex flex-col md:gap-2 gap-2">
                                                <div className="md:mb-2 md:mt-0 mt-2 font-semibold text-lg text-black">
                                                    {i18n({
                                                        EN: "Coverage",
                                                        SV: "Täckning",
                                                        PTBR: "Cobertura",
                                                        PT: "Cobertura",
                                                        PL: "Zakres",
                                                        ES: "Cobertura",
                                                        CZ: "Pokrytí",
                                                        FR: "Notre gamme",
                                                        IT: "Copertura",
                                                        RO: "Acoperire",
                                                        NL: "Dekking",
                                                    })}
                                                </div>

                                                <Bullet
                                                    icon={faChevronCircleRight}
                                                    title={i18n({
                                                        EN: "Photovoltaics",
                                                        SV: "Fotovoltaik",
                                                        PTBR: "Fotovoltaica",
                                                        PT: "Solar",
                                                        PL: "Fotowoltaika",
                                                        ES: "Fotovoltaica",
                                                        CZ: "Fotovoltaika",
                                                        FR: "Photovoltaïque",
                                                        IT: "Fotovoltaico",
                                                        RO: "Fotovoltaică",
                                                        NL: "Fotovoltaïsche energie",
                                                    })}
                                                    subtitle={i18n({
                                                        EN: "Automatic PV-roofing & inverter selection",
                                                        SV: "Automatiskt val av PV-tak och växelriktare",
                                                        PTBR: "Seleção automática de telhado fotovoltaico e inversor",
                                                        PT: "Seleção automática do sistema fotovoltaico e do inversor",
                                                        PL: "Automatyczne pokrycie dachu, wybór falownika",
                                                        ES: "Selección automática de tejados fotovoltaicos e inversores",
                                                        CZ: "Automatická volba fotovoltaické střechy a střídače",
                                                        FR: "Sélection automatique de la toiture photovoltaïque et de l'onduleur",
                                                        IT: "Selezione automatica del tetto fotovoltaico e dell'inverter",
                                                        RO: "Selectarea automată a acoperișului fotovoltaic și a invertorului",
                                                        NL: "Automatische PV-dakbedekking & omvormerselectie",
                                                    })}
                                                />

                                                <Bullet
                                                    icon={faChevronCircleRight}
                                                    title={i18n({
                                                        EN: "Energy storage",
                                                        SV: "Lagring av energi",
                                                        PTBR: "Armazenamento de energia",
                                                        PT: "Bateria",
                                                        PL: "Magazynowanie energii",
                                                        ES: "Almacenamiento de energía",
                                                        CZ: "Skladování energie",
                                                        FR: "Stockage d'énergie",
                                                        IT: "Accumulo di energia",
                                                        RO: "Stocarea energiei",
                                                        NL: "Energieopslag",
                                                    })}
                                                    subtitle={i18n({
                                                        EN: "Emergency & backup power capability",
                                                        SV: "Kapacitet för nöd- och reservkraft",
                                                        PTBR: "Capacidade de energia de emergência e de backup",
                                                        PT: "Armazenamento de energia de emergência e de reserva",
                                                        PL: "Możliwość zasilania awaryjnego i rezerwowego",
                                                        ES: "Capacidad de alimentación de emergencia y reserva",
                                                        CZ: "Možnost nouzového a záložního napájení",
                                                        FR: "Capacité d'alimentation d'urgence et de secours",
                                                        IT: "Capacità di alimentazione di emergenza e di backup",
                                                        RO: "Capacitate de alimentare de urgență și de rezervă",
                                                        NL: "Nood- en back-up-stroomvoorziening",
                                                    })}
                                                />

                                                <Bullet
                                                    icon={faChevronCircleRight}
                                                    title={i18n({
                                                        EN: "Wallbox",
                                                        SV: "Wallbox",
                                                        PTBR: "Wallbox",
                                                        PT: "Wallbox",
                                                        PL: "Wallbox",
                                                        ES: "Caja mural",
                                                        CZ: "Wallbox",
                                                        FR: "Bornes de recharge murales",
                                                        IT: "Wallbox",
                                                        RO: "Wallbox",
                                                        NL: "Wallbox",
                                                    })}
                                                    subtitle={i18n({
                                                        EN: "Consideration of smart charging",
                                                        SV: "Övervägande av smart laddning",
                                                        PTBR: "Consideração da cobrança inteligente",
                                                        PT: "Compatibilidade com carregamento inteligente",
                                                        PL: "Możliwość inteligentnego ładowania",
                                                        ES: "Consideración de la tarificación inteligente",
                                                        CZ: "Zohlednění inteligentního nabíjení",
                                                        FR: "Recharge intelligente",
                                                        IT: "Considerazione della ricarica intelligente",
                                                        RO: "Luarea în considerare a încărcării inteligente",
                                                        NL: "Overweging van slim laden",
                                                    })}
                                                />

                                                <Bullet
                                                    icon={faChevronCircleRight}
                                                    title={i18n({
                                                        EN: "Electric vehicles",
                                                        SV: "Elektriska fordon",
                                                        PTBR: "Veículos elétricos",
                                                        PT: "Veículos eléctricos",
                                                        PL: "Pojazdy elektryczne",
                                                        ES: "Vehículos eléctricos",
                                                        CZ: "Elektrická vozidla",
                                                        FR: "Véhicules électriques",
                                                        IT: "Veicoli elettrici",
                                                        RO: "Vehicule electrice",
                                                        NL: "Elektrische voertuigen",
                                                    })}
                                                    subtitle={i18n({
                                                        EN: "2,000+ vehicles available",
                                                        SV: "2.000+ fordon tillgängliga",
                                                        PTBR: "Mais de 2.000 veículos disponíveis",
                                                        PT: "Mais de 2.000 veículos disponíveis",
                                                        PL: "Ponad 2 000 dostępnych pojazdów",
                                                        ES: "Más de 2.000 vehículos disponibles",
                                                        CZ: "K dispozici je více než 2 000 vozidel",
                                                        FR: "Plus de 2 000 véhicules disponibles",
                                                        IT: "Oltre 2.000 veicoli disponibili",
                                                        RO: "2,000+ vehicule disponibile",
                                                        NL: ">2.000 voertuigen beschikbaar",
                                                    })}
                                                />

                                                <Bullet
                                                    icon={faChevronCircleRight}
                                                    title={i18n({
                                                        EN: "Heat pumps",
                                                        SV: "Värmepumpar",
                                                        PTBR: "Bombas de calor",
                                                        PT: "Bombas de calor",
                                                        PL: "Pompy ciepła",
                                                        ES: "Bombas de calor",
                                                        CZ: "Tepelná čerpadla",
                                                        FR: "Pompes à chaleur",
                                                        IT: "Pompe di calore",
                                                        RO: "Pompe de căldură",
                                                        NL: "Warmtepompen",
                                                    })}
                                                    subtitle={i18n({
                                                        EN: "Heat pump quick check, subsidy check, heat load calculation according to DIN12831 & more",
                                                        SV: "Snabbkontroll av värmepump, bidragskontroll, beräkning av värmebelastning enligt DIN12831 och mer",
                                                        PTBR: "Verificação rápida da bomba de calor, verificação de subsídios, cálculo da carga térmica de acordo com a norma DIN12831 e muito mais",
                                                        PT: "Verificação rápida da bomba de calor, subsídios, cálculo da carga térmica de acordo com a norma DIN12831, e mais",
                                                        PL: "Szybka analiza wykonalności, sprawdzenie dotacji, obliczenie obciążenia cieplnego i więcej",
                                                        ES: "Comprobación rápida de la bomba de calor, comprobación de la subvención, cálculo de la carga térmica según DIN12831 y mucho más",
                                                        CZ: "Rychlá kontrola tepelných čerpadel, kontrola dotací, výpočet tepelné zátěže podle DIN12831 a další.",
                                                        FR: "Vérification rapide des pompes à chaleur, vérification des subventions, calcul de la charge thermique selon la norme DIN12831, etc.",
                                                        IT: "Controllo rapido delle pompe di calore, controllo delle sovvenzioni, calcolo del carico termico secondo DIN12831 e altro ancora",
                                                        RO: "Verificarea rapidă a pompei de căldură, verificarea subvențiilor, calculul sarcinii termice în conformitate cu DIN12831 și multe altele",
                                                        NL: "Subsidiecheck, warmtelastberekening volgens DIN12831 & meer",
                                                    })}
                                                />
                                            </div>
                                            <div className="flex justify-center h-full">
                                                <div className="border-l h-full shrink-0" />
                                            </div>
                                            <div className="flex flex-col gap-2">
                                                <div className="md:mb-2 md:mt-0 mt-2 font-semibold text-lg text-black">
                                                    {i18n({
                                                        EN: "Process",
                                                        SV: "Process",
                                                        PTBR: "Processo",
                                                        PT: "Processo",
                                                        PL: "Proces",
                                                        ES: "Proceso",
                                                        CZ: "Proces",
                                                        FR: "Processus",
                                                        IT: "Processo",
                                                        RO: "Procesul",
                                                        NL: "Proces",
                                                    })}
                                                </div>
                                                <Bullet
                                                    number="1"
                                                    title={i18n({
                                                        EN: "Customer information",
                                                        SV: "Information till kunder",
                                                        PTBR: "Informações sobre o cliente",
                                                        PT: "Informação do cliente",
                                                        PL: "Informacje o kliencie",
                                                        ES: "Información al cliente",
                                                        CZ: "Informace pro zákazníky",
                                                        FR: "Informations sur le client",
                                                        IT: "Raccolta informazioni sui clienti",
                                                        RO: "Informații pentru clienți",
                                                        NL: "Klantinformatie",
                                                    })}
                                                    subtitle={i18n({
                                                        EN: "Interactive knowledge database",
                                                        SV: "Interaktiv kunskapsdatabas",
                                                        PTBR: "Banco de dados de conhecimento interativo",
                                                        PT: "Base de conhecimentos interactiva",
                                                        PL: "Interaktywna baza wiedzy",
                                                        ES: "Base de datos interactiva de conocimientos",
                                                        CZ: "Interaktivní znalostní databáze",
                                                        FR: "Base de données interactive",
                                                        IT: "Database digitale dei contatti",
                                                        RO: "Baza de date interactivă de cunoștințe",
                                                        NL: "Interactieve kennisdatabase",
                                                    })}
                                                />
                                                <Bullet
                                                    number="2"
                                                    title={i18n({
                                                        EN: "Lead generation",
                                                        SV: "Generering av leads",
                                                        PTBR: "Geração de leads",
                                                        PT: "Criação de novas leads",
                                                        PL: "Generowanie potencjalnych klientów",
                                                        ES: "Juhtide genereerimine",
                                                        CZ: "Generování olova",
                                                        FR: "Recherche de prospects",
                                                        IT: "Generazione dei lead",
                                                        RO: "Generarea de lead-uri",
                                                        NL: "Genereren van leads",
                                                    })}
                                                />
                                                <Bullet
                                                    number="3"
                                                    title={i18n({
                                                        EN: "Lead qualification",
                                                        SV: "Kvalificering av leads",
                                                        PTBR: "Qualificação de leads",
                                                        PT: "Qualificação de leads",
                                                        PL: "Kwalifikacja potencjalnych klientów",
                                                        ES: "cualificación de clientes potenciales",
                                                        CZ: "kvalifikace vedení",
                                                        FR: "Qualification des prospects",
                                                        IT: "Qualificazione dei lead",
                                                        RO: "Calificarea liderilor",
                                                        NL: "Leadkwalificatie",
                                                    })}
                                                    subtitle={i18n({
                                                        EN: "Intelligent data enquiry and evaluation",
                                                        SV: "Intelligent dataförfrågan och utvärdering",
                                                        PTBR: "Consulta e avaliação inteligente de dados",
                                                        PT: "Solicitação e avaliação inteligente de dados",
                                                        PL: "Inteligentny formularz i analiza danych",
                                                        ES: "Consulta y evaluación inteligente de datos",
                                                        CZ: "Inteligentní zjišťování a vyhodnocování dat",
                                                        FR: "Enquête et évaluation intelligentes des données",
                                                        IT: "Ricerca e valutazione intelligente dei dati",
                                                        RO: "Căutarea și evaluarea inteligentă a datelor",
                                                        NL: "Intelligente gegevensopvraging en -evaluatie",
                                                    })}
                                                />
                                                <Bullet
                                                    number="4"
                                                    title={i18n({
                                                        EN: "Consultation",
                                                        SV: "Konsultation",
                                                        PTBR: "Consulta",
                                                        PT: "Reunião",
                                                        PL: "Doradztwo",
                                                        ES: "Consulta",
                                                        CZ: "Konzultace",
                                                        FR: "Consultation",
                                                        IT: "Consultazione su appuntamento",
                                                        RO: "Consultare",
                                                        NL: "Advies",
                                                    })}
                                                />
                                                <Bullet
                                                    number="5"
                                                    title={i18n({
                                                        EN: "Layout & planning",
                                                        SV: "Layout & planering",
                                                        PTBR: "Layout e planejamento",
                                                        PT: "Layout e planeamento",
                                                        PL: "Układ i planowanie",
                                                        ES: "Diseño y planificación",
                                                        CZ: "Rozvržení a plánování",
                                                        FR: "Configuration et planification",
                                                        IT: "Layout e pianificazione",
                                                        RO: "Amenajare și planificare",
                                                        NL: "Layout & planning",
                                                    })}
                                                    subtitle={i18n({
                                                        EN: "Consideration of offer variants",
                                                        SV: "Övervägande av olika varianter av erbjudanden",
                                                        PTBR: "Consideração de variantes de oferta",
                                                        PT: "Consideração de variantes de oferta",
                                                        PL: "Uwzględnienie różnych wariantów oferty",
                                                        ES: "Consideración de variantes de oferta",
                                                        CZ: "Zvažování variant nabídky",
                                                        FR: "Prise en compte des variantes de l'offre",
                                                        IT: "Esame delle varianti e delle offerte",
                                                        RO: "Luarea în considerare a variantelor de ofertă",
                                                        NL: "Overweging van offertevarianten",
                                                    })}
                                                />
                                                <Bullet
                                                    number="6"
                                                    title={i18n({
                                                        EN: "Digital signature",
                                                        SV: "Digital signatur",
                                                        PTBR: "Assinatura digital",
                                                        PT: "Assinatura digital",
                                                        PL: "Podpis elektroniczny",
                                                        ES: "Firma digital",
                                                        CZ: "Digitální podpis",
                                                        FR: "Signature numérique",
                                                        IT: "Preventivo e firma digitale",
                                                        RO: "Semnătura digitală",
                                                        NL: "Digitale handtekening",
                                                    })}
                                                    subtitle={i18n({
                                                        EN: "Automatic PDF offer",
                                                        SV: "Automatiskt PDF-erbjudande",
                                                        PTBR: "Oferta automática em PDF",
                                                        PT: "Oferta automática em PDF",
                                                        PL: "Automatyczna generacja oferty w formacie PDF",
                                                        ES: "Oferta automática de PDF",
                                                        CZ: "Automatická nabídka PDF",
                                                        FR: "Génération automatique d'une offre au format PDF",
                                                        IT: "Creazione automatica di PDF",
                                                        RO: "Ofertă automată PDF",
                                                        NL: "Automatische pdf-offerte",
                                                    })}
                                                />
                                            </div>
                                            <div className="flex justify-center h-full">
                                                <div className="border-l h-full shrink-0" />
                                            </div>
                                            <div className="flex flex-col gap-2">
                                                <div className="md:mb-2 md:mt-0 mt-2 font-semibold text-lg text-black">
                                                    {i18n({
                                                        EN: "Features",
                                                        SV: "Funktioner",
                                                        PTBR: "Recursos",
                                                        PT: "Funcionalidades",
                                                        PL: "Cechy",
                                                        ES: "Características",
                                                        CZ: "Funkce",
                                                        FR: "Caractéristiques",
                                                        IT: "Caratteristiche",
                                                        RO: "Caracteristici",
                                                        NL: "Functies",
                                                    })}
                                                </div>
                                                <Bullet
                                                    title={i18n({
                                                        EN: "Kanban project management",
                                                        SV: "Kanban projektledning",
                                                        PTBR: "Gerenciamento de projetos Kanban",
                                                        PT: "Gestão de projectos Kanban",
                                                        PL: "Zarządzanie projektami",
                                                        ES: "Gestión de proyectos Kanban",
                                                        CZ: "Řízení projektů Kanban",
                                                        FR: "Méthode Kanban",
                                                        IT: "Gestione del progetto Kanban",
                                                        RO: "Gestionarea proiectelor Kanbant",
                                                        NL: "Kanban projectmanagement",
                                                    })}
                                                    subtitle={i18n({
                                                        EN: "For seamless CRM",
                                                        SV: "För sömlös CRM",
                                                        PTBR: "Para um CRM perfeito",
                                                        PT: "Para um CRM elegante",
                                                        PL: "Dla sprawnego zarządzania relacjami z klientami",
                                                        ES: "Para un CRM sin fisuras",
                                                        CZ: "Pro bezproblémové CRM",
                                                        FR: "Pour une gestion de la relation client sans faille",
                                                        IT: "Per un CRM senza soluzione di continuità",
                                                        RO: "Pentru CRM fără întreruperi",
                                                        NL: "Voor naadloos CRM",
                                                    })}
                                                    icon={faCheckCircle}
                                                />
                                                <Bullet
                                                    title={i18n({
                                                        EN: "Notes & Notifications",
                                                        SV: "Anteckningar och meddelanden",
                                                        PTBR: "Notas e notificações",
                                                        PT: "Notas e notificações",
                                                        PL: "Notatki i powiadomienia",
                                                        ES: "Notas y notificaciones",
                                                        CZ: "Poznámky a oznámení",
                                                        FR: "Notes et notifications",
                                                        IT: "Note e notifiche",
                                                        RO: "Note și notificări",
                                                        NL: "Opmerkingen & meldingen",
                                                    })}
                                                    icon={faCheckCircle}
                                                    subtitle={i18n({
                                                        EN: "Individually customisable",
                                                        SV: "Individuellt anpassningsbar",
                                                        PTBR: "Individualmente personalizável",
                                                        PT: "Personalizável individualmente",
                                                        PL: "Możliwość indywidualnego dostosowania",
                                                        ES: "Personalizable individualmente",
                                                        CZ: "Individuální přizpůsobení",
                                                        FR: "Personnalisable individuellement",
                                                        IT: "Personalizzabile individualmente",
                                                        RO: "Personalizabil individual",
                                                        NL: "Individueel aanpasbaar",
                                                    })}
                                                />
                                                <Bullet
                                                    title={i18n({
                                                        EN: "Profitability calculation",
                                                        SV: "Lönsamhetskalkyl",
                                                        PTBR: "Cálculo de rentabilidade",
                                                        PT: "Cálculo de rentabilidade",
                                                        PL: "Obliczanie rentowności",
                                                        ES: "Cálculo de la rentabilidad",
                                                        CZ: "Výpočet ziskovosti",
                                                        FR: "Calcul de la rentabilité",
                                                        IT: "Calcolo della redditività",
                                                        RO: "Calculul profitabilității",
                                                        NL: "Rentabiliteitsberekening",
                                                    })}
                                                    icon={faCheckCircle}
                                                />
                                                <Bullet
                                                    title={i18n({
                                                        EN: "Energy system assessment",
                                                        SV: "Bedömning av energisystem",
                                                        PTBR: "Avaliação do sistema de energia",
                                                        PT: "Avaliação do sistema energético",
                                                        PL: "Przegląd źródeł energii",
                                                        ES: "Evaluación del sistema energético",
                                                        CZ: "Posouzení energetického systému",
                                                        FR: "Évaluation des systèmes énergétiques",
                                                        IT: "Valutazione del sistema energetico",
                                                        RO: "Evaluarea sistemului energetic",
                                                        NL: "Beoordeling energiesysteem",
                                                    })}
                                                    icon={faCheckCircle}
                                                />
                                                <Bullet
                                                    title={i18n({
                                                        EN: "Collaborative",
                                                        SV: "Samarbete",
                                                        PTBR: "Colaborativo",
                                                        PT: "Colaboração",
                                                        PL: "Współpraca",
                                                        ES: "Colaboración",
                                                        CZ: "Spolupráce",
                                                        FR: "Collaboratif",
                                                        IT: "Collaborativo",
                                                        RO: "Colaborare",
                                                        NL: "Coöperatief",
                                                    })}
                                                    icon={faCheckCircle}
                                                    subtitle={i18n({
                                                        EN: "Individual role allocation",
                                                        SV: "Individuell rollfördelning",
                                                        PTBR: "Alocação de função individual",
                                                        PT: "Atribuição individual de funções",
                                                        PL: "Indywidualny przydział ról",
                                                        ES: "Asignación individual de funciones",
                                                        CZ: "Přidělení jednotlivých rolí",
                                                        FR: "Attribution de rôle individuel",
                                                        IT: "Assegnazione del ruolo individuale",
                                                        RO: "Alocarea individuală a rolurilor",
                                                        NL: "Individuele roltoewijzing",
                                                    })}
                                                />
                                                <Bullet
                                                    title={i18n({
                                                        EN: "White-Label",
                                                        SV: "White-Label",
                                                        PTBR: "Etiqueta branca",
                                                        PT: "White-Label",
                                                        PL: "White-Label",
                                                        ES: "White-Label",
                                                        CZ: "White-Label",
                                                        FR: "White-Label",
                                                        IT: "White-Label",
                                                        RO: "Etichetă albă",
                                                        NL: "White-Label",
                                                    })}
                                                    icon={faCheckCircle}
                                                    subtitle={i18n({
                                                        EN: "Customizable to your CI",
                                                        SV: "Anpassningsbar till ditt CI",
                                                        PTBR: "Personalizável de acordo com seu IC",
                                                        PT: "Personalizável de acordo com o seu branding",
                                                        PL: "Możliwość personalizacji wizualnej dopasowanej do Twojej marki",
                                                        ES: "Adaptable a su IC",
                                                        CZ: "Přizpůsobení vašemu CI",
                                                        FR: "Personnalisable en fonction de votre entreprise",
                                                        IT: "Personalizzabile in base alla vostra CI",
                                                        RO: "Personalizabil pentru IC",
                                                        NL: "Aanpasbaar aan uw huisstijl",
                                                    })}
                                                />
                                                <Bullet
                                                    title={i18n({
                                                        EN: "Desktop and mobile access",
                                                        SV: "Tillgång till dator och mobil",
                                                        PTBR: "Acesso para desktop e celular",
                                                        PT: "Acessível no computador e dispositivos móveis",
                                                        PL: "Dostęp z komputerów stacjonarnych i urządzeń mobilnych",
                                                        ES: "Acceso desde ordenadores y móviles",
                                                        CZ: "Přístup ke stolním počítačům a mobilním zařízením",
                                                        FR: "Accessible par ordinateur et mobile",
                                                        IT: "Accesso da desktop e mobile",
                                                        RO: "Acces desktop și mobil",
                                                        NL: "Desktop- en mobiele toegang",
                                                    })}
                                                    subtitle={i18n({
                                                        EN: "Optimized for tablet usage",
                                                        SV: "Optimerad för användning på surfplatta",
                                                        PTBR: "Otimizado para uso em tablets",
                                                        PT: "Optimizado para utilização no tablet",
                                                        PL: "Zoptymalizowany dla tabletów",
                                                        ES: "Optimizado para tabletas",
                                                        CZ: "Optimalizováno pro použití na tabletu",
                                                        FR: "Optimisé pour les tablettes",
                                                        IT: "Ottimizzato per l'uso di tablet",
                                                        RO: "Optimizat pentru utilizarea pe tabletă",
                                                        NL: "Geoptimaliseerd voor tabletgebruik",
                                                    })}
                                                    icon={faCheckCircle}
                                                />
                                                <Bullet
                                                    title={i18n({
                                                        EN: "Kompontent databse",
                                                        SV: "Kompletterande databaser",
                                                        PTBR: "Banco de dados de conteúdo",
                                                        PT: "Base de dados de componentes",
                                                        PL: "Baza danych komponentów",
                                                        ES: "Base de datos de contenidos",
                                                        CZ: "Kompontentní databáze",
                                                        FR: "Base de données",
                                                        IT: "Dati di contenuto",
                                                        RO: "Conținutul bazei de date",
                                                        NL: "Componentendatabase",
                                                    })}
                                                    subtitle={i18n({
                                                        EN: "40.000+ entries from 1000+ manufacturers",
                                                        SV: "40.000+ poster från 1000+ tillverkare",
                                                        PTBR: "Mais de 40.000 entradas de mais de 1.000 fabricantes",
                                                        PT: "Mais de 40.000 entradas, de mais de 1000 fabricantes",
                                                        PL: "Ponad 40 000 komponentów od ponad 1000 producentów",
                                                        ES: "Más de 40.000 entradas de más de 1.000 fabricantes",
                                                        CZ: "Více než 40 000 položek od více než 1000 výrobců",
                                                        FR: "Plus de 40 000 composants provenant de plus de 1000 fabricants",
                                                        IT: "40.000 voci da oltre 1000 produttori",
                                                        RO: "40.000+ intrări de la peste 1000+ producători",
                                                        NL: ">40.000 items van >1000 fabrikanten",
                                                    })}
                                                    icon={faCheckCircle}
                                                />
                                            </div>
                                        </div>
                                        <div className="flex gap-2 mt-3">
                                            <LinkAsButton
                                                className="bg-zinc-100 hover:bg-zinc-200 text-zinc-700 hover:text-black w-fit"
                                                to={locPath(locale, "/book-a-meeting/")}
                                            >
                                                {i18n({
                                                    EN: "Get a demo",
                                                    SV: "Boka en demo",
                                                    PTBR: "Agendar uma demonstração",
                                                    PT: "Pedir demonstração",
                                                    PL: "Umów demo",
                                                    ES: "Solicite una demo",
                                                    CZ: "Získejte ukázku",
                                                    FR: "Demander une démo",
                                                    IT: "Richiedi una demo",
                                                    RO: "Obțineți o demonstrație",
                                                    NL: "Vraag een demo aan",
                                                })}
                                            </LinkAsButton>
                                        </div>
                                    </motion.div>
                                )}
                            </div>
                        </AnimatePresence>
                    </div>
                </AnimatePresence>
            </Section>

            <Section outerClassName="py-24 md:py-44 lg:py-52">
                <div className="grid md:grid-cols-2 gap-10 items-center" ref={slideshow.ref}>
                    <div className="col-span-full mb-2">
                        <h2 className="text-black font-bold text-3xl sm:text-4xl md:text-5xl">
                            {i18n({
                                EN: "More than just software.",
                                SV: "Mer än bara programvara.",
                                PTBR: "Mais do que apenas software.",
                                PT: "Mais do que apenas software.",
                                PL: "Więcej niż tylko oprogramowanie.",
                                ES: "Más que software.",
                                CZ: "Více než jen software.",
                                FR: "Plus qu'un simple logiciel.",
                                IT: "Più di un semplice software.",
                                RO: "Mai mult decât un simplu software.",
                                NL: "Meer dan alleen software.",
                            })}
                        </h2>
                        <h2 className="text-zinc-600 font-medium text-3xl sm:text-4xl md:text-5xl mt-[4px]">
                            {i18n({
                                EN: "Your entire customer journey in one solution.",
                                SV: "Hela din kundresa i en och samma lösning.",
                                PTBR: "Toda a jornada do cliente em uma única plataforma.",
                                PT: "Toda a jornada do cliente numa única solução.",
                                PL: "Cała podróż klienta w jednym rozwiązaniu.",
                                ES: "Todo el recorrido del cliente en una sola solución.",
                                CZ: "Celá vaše cesta k zákazníkovi v jednom řešení.",
                                FR: "L'ensemble de votre parcours client à portée de main.",
                                IT: "L'intera user journey del cliente in un'unica soluzione.",
                                RO: "Întreaga călătorie a clientului într-o singură soluție.",
                                NL: "Het volledige klanttraject in één oplossing.",
                            })}
                        </h2>
                    </div>

                    <div>
                        <div className="rounded-lg relative flex flex-col justify-center w-[100%] h-[300px] md:h-[600px] shadow-lg overflow-hidden">
                            <Img
                                className="w-full h-full object-cover object-top"
                                alt=""
                                sizeMobile="sm"
                                size="md"
                                src={
                                    slideshow.slideNr === 0
                                        ? "/pages/_assets/mockup_01.png"
                                        : slideshow.slideNr === 1
                                          ? "/pages/_assets/mockup_02.png"
                                          : slideshow.slideNr === 2
                                            ? "/pages/_assets/mockup_03.png"
                                            : "/pages/_assets/mockup_04.png"
                                }
                            />
                        </div>
                        <div className="w-full flex justify-center gap-2 mt-10">
                            <Thumb
                                twColor="bg-reonic-yellow"
                                onClick={() => slideshow.showSlide(0)}
                                selected={slideshow.slideNr === 0}
                            />
                            <Thumb
                                twColor="bg-reonic-red"
                                onClick={() => slideshow.showSlide(1)}
                                selected={slideshow.slideNr === 1}
                            />
                            <Thumb
                                twColor="bg-reonic-blue"
                                onClick={() => slideshow.showSlide(2)}
                                selected={slideshow.slideNr === 2}
                            />
                            <Thumb
                                twColor="bg-reonic-green"
                                onClick={() => slideshow.showSlide(3)}
                                selected={slideshow.slideNr === 3}
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-1 gap-10 pb-10">
                        <button onClick={() => slideshow.showSlide(0)} className="text-left">
                            <div
                                className={`
                                    font-bold transition-all duration-300 mb-2 leading-6 text-black 
                                    ${slideshow.slideNr === 0 && "focus"}
                                    text-xl [&.focus]:text-2xl [&.focus]:text-reonic-yellow 
                                `}
                            >
                                {i18n({
                                    EN: "Inform & \n generate leads.",
                                    SV: "Informera & \n generera leads.",
                                    PTBR: "Informar e \n gerar leads.",
                                    PT: "Informe e \n gere leads.",
                                    PL: "Doradzaj i \n zdobywaj potencjalnych klientów.",
                                    ES: "Informar y \n generar clientes potenciales.",
                                    CZ: "Informujte a \n generujte potenciální zákazníky.",
                                    FR: "Informer et \n générer des prospects.",
                                    IT: "Informa e \n genera contatti.",
                                    RO: "Informare și generare de potențiali clienți.",
                                    NL: "Informeer & \n genereer leads.",
                                })}
                            </div>
                            <hr className="mb-3 mt-3" />
                            <div className="text-sm text-zinc-600">
                                {i18n({
                                    EN: "Use our 360° elements to inform your customers digitally. With our Energy House, you can generate pre-qualified online customer enquiries online.",
                                    SV: "Använd våra 360°-element för att informera dina kunder digitalt. Med vårt Energy House kan du generera förkvalificerade kundförfrågningar online.",
                                    PTBR: "Use os nossos elementos 360° para se comunicar e gerar cotações com leads pré-qualificados 100% online",
                                    PT: "Utilize os nossos elementos 360° para informar digitalmente os seus clientes. Com a nossa e-Casa, pode gerar pedidos de informação pré-qualificados de clientes online.",
                                    PL: "Skorzystaj z naszych rozwiązań 360°, aby świadczyć porady dla swoich klientów online. Nasze oprogramowanie umożliwia generowanie wstępnie zakwalifikowanych zapytań klientów.",
                                    ES: "Utilice nuestros elementos 360° para informar digitalmente a sus clientes. Con nuestra Energy House, puede generar consultas online de clientes precalificados.",
                                    CZ: "Využijte naše prvky 360° k digitálnímu informování zákazníků. S naším Energy House můžete generovat předem kvalifikované online dotazy zákazníků online.",
                                    FR: "Profitez de nos solutions à 360° pour conseiller vos clients en ligne. Notre logiciel permet de générer des demandes de renseignements de clients préalablement qualifiés.",
                                    IT: "Informa i tuoi clienti in modo digitale con i nostri strumenti a 360°. Con la nostra Energy House, puoi generare online richieste da clienti pre-qualificati.",
                                    RO: "Utilizați elementele noastre 360° pentru a vă informa digital clienții. Cu Energy House, puteți genera online cereri de informații precalificate pentru clienți.",
                                    NL: "Gebruik onze 360° elementen om uw klanten digitaal te informeren. Met onze Energy House kunt u online voorgekwalificeerde klantaanvragen genereren.",
                                })}
                            </div>
                        </button>
                        <button onClick={() => slideshow.showSlide(1)} className="text-left">
                            <div
                                className={`
                                    font-bold transition-all duration-300 mb-2 leading-6 text-black 
                                    ${slideshow.slideNr === 1 && "focus"}
                                    text-xl [&.focus]:text-2xl [&.focus]:text-reonic-red 
                                `}
                            >
                                {i18n({
                                    EN: "Plan. Advise. \n Inspire.",
                                    SV: "Planera. Rådge. Inspirera",
                                    PTBR: "Planeje. Aconselhe. \n Inspire.",
                                    PT: "Planeie. Aconselhe. \n Inspire.",
                                    PL: "Planuj. Doradzaj. \n Inspiruj.",
                                    ES: "Planea, aconseja e inspira.",
                                    CZ: "Plán. Poradenství. \n Inspirujte.",
                                    FR: "Planifier. Conseiller. \n Inspirer.",
                                    IT: "Semplifica la vendita",
                                    RO: "Planifică. Consiliază. \n Inspiră.",
                                    NL: "Plan. Adviseer. \n Inspireer.",
                                })}
                            </div>
                            <hr className="mb-3 mt-3" />
                            <div className="text-sm text-zinc-600">
                                {i18n({
                                    EN: "From request to offer in five minutes. Reonic is digital, intuitive, collaborative and browser-based.",
                                    SV: "Från förfrågan till erbjudande på fem minuter. Reonic är digitalt, intuitivt, samarbetsinriktat och webbläsarbaserat.",
                                    PTBR: "Da solicitação à oferta em cinco minutos. O Reonic é digital, intuitivo, colaborativo e baseado em navegador.",
                                    PT: "Desde o pedido inicial à oferta em cinco minutos. O Portal é digital, intuitivo, e colaborativo, tudo a partir do seu browser.",
                                    PL: "Od zapytania do oferty w pięć minut. Reonic jest intuicyjnym rozwiązaniem do współpracy online.",
                                    ES: "De la solicitud a la propuesta en 5 minutos. Reonic es digital, intuitivo, colaborativo y en la web.",
                                    CZ: "Od žádosti k nabídce za pět minut. Reonic je digitální, intuitivní, spolupracující a založený na prohlížeči.",
                                    FR: "De la demande à l'offre en cinq minutes. Reonic est une solution intuitive pour la collaboration en ligne.",
                                    IT: "Dalla richiesta all'offerta in cinque minuti. Reonic è digitale, intuitivo, collaborativo e basato su browser.",
                                    RO: "De la cerere la ofertă în cinci minute. Reonic este digital, intuitiv, colaborativ și bazat pe browser.",
                                    NL: "Van aanvraag tot offerte in vijf minuten. Reonic is digitaal, intuïtief, samenwerkingsgericht en werkt in de browser.",
                                })}
                            </div>
                        </button>
                        <button onClick={() => slideshow.showSlide(2)} className="text-left">
                            <div
                                className={`
                                    font-bold transition-all duration-300 mb-2 leading-6 text-black 
                                    ${slideshow.slideNr === 2 && "focus"}
                                    text-xl [&.focus]:text-2xl [&.focus]:text-reonic-blue 
                                `}
                            >
                                {i18n({
                                    EN: "Secure contracts. \n  100% digital.",
                                    SV: "Säkra avtal. \n 100% digitalt.",
                                    PTBR: "Contratos seguros. \n 100% digital.",
                                    PT: "Contratos seguros. \n  100% digital.",
                                    PL: "Bezpieczne umowy. \n 100% online.",
                                    ES: "Contratos seguros. \n  100% digital.",
                                    CZ: "Zabezpečení smluv. \n  100% digitální.",
                                    FR: "Contrats sécurisés. \n  100% digital.",
                                    IT: "Contratti sicuri. \n 100% digitali.",
                                    RO: "Contracte sigure. \n 100% digital.",
                                    NL: "Veilige contracten. \n 100% digitaal.",
                                })}
                            </div>
                            <hr className="mb-3 mt-3" />
                            <div className="text-sm text-zinc-600">
                                {i18n({
                                    EN: "Impress your customers with serious and convincing offers in PDF or digital form.",
                                    SV: "Imponera på dina kunder med seriösa och övertygande erbjudanden i PDF eller digital form.",
                                    PTBR: "Impressione seus clientes com ofertas sérias e convincentes em formato PDF ou digital.",
                                    PT: "Impressione os seus clientes com ofertas detalhadas e convincentes em PDF ou em formato digital.",
                                    PL: "Zaimponuj swoim klientom ofertami online lub w formacie PDF.</>",
                                    ES: "Impresione a sus clientes con ofertas serias y convincentes en formato PDF o digital.",
                                    CZ: "Zapůsobte na své zákazníky seriózními a přesvědčivými nabídkami ve formátu PDF nebo v digitální podobě.",
                                    FR: "Impressionnez vos clients avec des offres sérieuses et convaincantes au format PDF ou digital.",
                                    IT: "Stupisci i tuoi clienti con offerte serie e convincenti in formato PDF o digitale.",
                                    RO: "Impresionați clienții voștri cu oferte serioase și convingătoare în format PDF sau digital.",
                                    NL: "Maak indruk op uw klanten met serieuze en overtuigende offertes in pdf of digitale vorm.",
                                })}
                            </div>
                        </button>
                        <button onClick={() => slideshow.showSlide(3)} className="text-left">
                            <div
                                className={`
                                    font-bold transition-all duration-300 mb-2 leading-6 text-black 
                                    ${slideshow.slideNr === 3 && "focus"}
                                    text-xl [&.focus]:text-2xl [&.focus]:text-reonic-green 
                                `}
                            >
                                {i18n({
                                    EN: "Install. Process. \n Organize.",
                                    SV: "Installera. Processera. \n Organisera.",
                                    PTBR: "Instalar. Processar. \n Organizar.",
                                    PT: "Instale. Processe. \n Organize.",
                                    PL: "Instaluj. Realizuj. \n Organizuj.",
                                    ES: "Instalar. Procesar. \n Organizar.",
                                    CZ: "Instalace. Proces. \n Uspořádat.",
                                    FR: "Installer. Traiter. \n Organiser.",
                                    IT: "Prepara, realizza, \n installa.",
                                    RO: "Instalează. Procesează. \n Organizează.",
                                    NL: "Installeer. Verwerk. \n Organiseer.",
                                })}
                            </div>
                            <hr className="mb-3 mt-3" />
                            <div className="text-sm text-zinc-600">
                                {i18n({
                                    EN: "Always stay on top of new offers, current plans and ongoing orders.",
                                    SV: "Håll dig alltid uppdaterad om nya erbjudanden, aktuella planer och pågående beställningar.",
                                    PTBR: "Sempre fique por dentro das novas ofertas, dos planos atuais e dos pedidos em andamento.",
                                    PT: "Mantenha-se a par de novas ofertas, planeamentos, e encomendas em curso.",
                                    PL: "Bądź zawsze na bieżąco z nowymi ofertami, aktualnymi planami i bieżącymi zamówieniami.</>",
                                    ES: "Manténgase siempre al día de las nuevas ofertas, los planes actuales y los pedidos en curso.",
                                    CZ: "Mějte vždy přehled o nových nabídkách, aktuálních plánech a probíhajících objednávkách.",
                                    FR: "Restez toujours au courant des nouvelles offres ainsi que des projets et commandes en cours.",
                                    IT: "Rimani sempre aggiornato su nuovi preventivi, offerte e ordini in corso.",
                                    RO: "Rămâneți întotdeauna la curent cu noile oferte, planurile actuale și comenzile în curs.",
                                    NL: "Blijf altijd op de hoogte van nieuwe aanbiedingen, huidige plannen en lopende bestellingen.",
                                })}
                            </div>
                        </button>
                    </div>
                </div>
            </Section>

            <Section gray outerClassName="py-24 md:py-44 lg:py-44">
                <div className="mb-14">
                    <h2 className="font-bold text-3xl sm:text-4xl md:text-5xl">
                        {i18n({
                            EN: "Why Reonic?",
                            SV: "Så varför Reonic?",
                            PTBR: "Então, por que a Reonic?",
                            PT: "Porquê a Reonic?",
                            PL: "Dlaczego Reonic?",
                            ES: "¿Por qué Reonic?",
                            CZ: "Proč tedy Reonic?",
                            FR: "Pourquoi Reonic ?",
                            IT: "Perché Reonic?",
                            RO: "Deci, de ce Reonic?",
                            NL: "Dus waarom Reonic?",
                        })}
                    </h2>
                    <h2 className="font-medium text-zinc-500 text-3xl sm:text-4xl md:text-5xl mt-[4px]">
                        {i18n({
                            EN: "Because you want the best solution.",
                            SV: "För att du vill ha den bästa lösningen.",
                            PTBR: "Porque você quer a melhor solução.",
                            PT: "Porque precisa da melhor solução.",
                            PL: "Ponieważ chcesz najlepszego rozwiązania.",
                            ES: "Porque quieres la mejor solución.",
                            CZ: "Protože chcete to nejlepší řešení.",
                            FR: "Parce que vous méritez la meilleure solution.",
                            IT: "Perché volete la soluzione migliore.",
                            RO: "Pentru că doriți cea mai bună soluție.",
                            NL: "Omdat u de beste oplossing wilt.",
                        })}
                    </h2>
                </div>
                {/* <div className="font-bold text-xl">Für Installateure und Planer</div> */}
                <div className="grid md:grid-cols-3 gap-14 mt-5">
                    <div className="flex flex-col gap-3">
                        <div className="shrink-0 grow-0  pt-4 pb-1 md:py-4 w-full rounded-lg flex items-end relative">
                            <Counter digits={2} className="text-6xl font-bold " from={10} to={90} />
                            <div className="text-3xl mb-[2px]">%</div>
                        </div>
                        <div className="font-bold text-xl leading-6">
                            {i18n({
                                EN: "Faster",
                                SV: "Snabbare",
                                PTBR: "Mais rápido",
                                PT: "Mais rápido",
                                PL: "Szybciej",
                                ES: "Más rápido",
                                CZ: "Rychlejší",
                                FR: "Plus rapide",
                                IT: "Più veloce",
                                RO: "Mai rapid",
                                NL: "Sneller",
                            })}
                        </div>
                        <div className="text-zinc-500 text-sm">
                            {i18n({
                                EN: "Reduce your quotation and planning periods by up to 90%. This leaves you with more time for customer contact and project realisation.",
                                SV: "Förkorta offert- och planeringstiderna med upp till 90%. Det ger dig mer tid för kundkontakt och projektgenomförande.",
                                PTBR: "Reduza seus períodos de cotação e planejamento em até 90%. Isso deixa você com mais tempo para o contato com o cliente e a realização do projeto.",
                                PT: "Reduza o tempo de preparação de orçamentos e de planeamento em até 90%. Utilize o seu tempo para o que verdadeiramente interessa - contacto com o cliente e materialização de projectos.",
                                PL: "Skróć czas wyceny i planowania nawet o 90%. Pozostawia Ci to więcej czasu na kontakt z klientem i realizację projektu.",
                                ES: "Reduzca sus plazos de oferta y planificación hasta en un 90%. Esto le deja más tiempo para el contacto con el cliente y la realización del proyecto.",
                                CZ: "Zkrácení doby pro tvorbu nabídek a plánování až o 90 %. Zůstane vám tak více času na kontakt se zákazníkem a realizaci projektu.",
                                FR: "Réduisez les délais de vos devis et planifications jusqu'à 90 %. Vous avez ainsi plus de temps pour les relations clients et la réalisation des projets.",
                                IT: "Riduci i tempi di preventivazione e pianificazione fino al 90%. Questo ti lascia più tempo per il contatto con i clienti e la realizzazione dei progetti.",
                                RO: "Reduceți perioadele de ofertare și planificare cu până la 90%. Acest lucru vă lasă mai mult timp pentru contactul cu clienții și realizarea proiectelor.",
                                NL: "Verkort uw offerte- en planningsperiodes met tot wel 90%. Dit geeft u meer tijd voor klantcontact en projectrealisatie.",
                            })}
                        </div>
                    </div>
                    <div className="flex flex-col gap-3">
                        <div className="shrink-0 grow-0 pt-4 pb-1 md:py-4 w-full rounded-lg flex items-end relative">
                            <Counter digits={2} className="text-6xl font-bold" from={10} to={30} />
                            <div className="text-3xl mb-[2px]">%</div>
                        </div>
                        <div className="font-bold text-xl leading-6">
                            {i18n({
                                EN: "More efficient",
                                SV: "Mer effektiv",
                                PTBR: "Mais eficiente",
                                PT: "Mais eficiente",
                                PL: "Większa wydajność",
                                ES: "Más eficaz",
                                CZ: "Efektivnější",
                                FR: "Plus efficace",
                                IT: "Più efficace",
                                RO: "Mai eficient",
                                NL: "Efficiënter",
                            })}
                        </div>
                        <div className="text-zinc-500 text-sm">
                            {i18n({
                                EN: "Achieve up to 30% higher conversion rates by pre-qualifying leads in our seamless digital customer journey.",
                                SV: "Uppnå upp till 30% högre konverteringsgrad genom att förkvalificera leads i vår sömlösa digitala kundresa.",
                                PTBR: "Obtenha taxas de conversão até 30% mais altas por meio da pré-qualificação de leads em nossa jornada digital contínua do cliente.",
                                PT: "Consiga taxas de conversão até 30% mais elevadas através da pré-qualificação de leads com a nossa jornada do cliente totalmente digital.",
                                PL: "Osiągnij do 30% wyższe współczynniki konwersji dzięki przyjaznej użytkownikowi wstępnej kwalifikacji potencjalnych klientów.",
                                ES: "Consiga hasta un 30% más de tasas de conversión mediante la precalificación de clientes potenciales en nuestro recorrido digital del cliente sin fisuras.",
                                CZ: "Dosáhněte až o 30 % vyšší míry konverze díky předběžné kvalifikaci potenciálních zákazníků v naší bezproblémové digitální zákaznické cestě.",
                                FR: "Atteignez des taux de conversion jusqu'à 30 % plus élevés en préqualifiant les prospects grâce à notre parcours client fluide et transparent.",
                                IT: "Ottieni tassi di conversione più alti fino al 30%, pre-qualificando i lead nel nostro percorso digitale senza interruzioni.",
                                RO: "Atingeți rate de conversie cu până la 30% mai mari prin precalificarea clienților potențiali în călătoria noastră digitală fără întreruperi cu clienții.",
                                NL: "Behaal tot 30% hogere conversiepercentages door prekwalificatie van leads in onze naadloze digitale klanttraject.",
                            })}
                        </div>
                    </div>
                    <div className="flex flex-col gap-3">
                        <div className="shrink-0 grow-0 pt-4 pb-1 md:py-4 w-full rounded-lg flex items-end relative">
                            <Counter digits={3} className="text-6xl font-bold" from={50} to={300} />
                            <div className="text-3xl mb-[2px]">%</div>
                        </div>
                        <div className="font-bold text-xl leading-6">
                            {i18n({
                                EN: "More profitable",
                                SV: "Mer lönsamt",
                                PTBR: "Mais rentável",
                                PT: "Mais rentável",
                                PL: "Bardziej opłacalnie",
                                ES: "Más rentable",
                                CZ: "Výnosnější",
                                FR: "Plus rentable",
                                IT: "Più redditizio",
                                RO: "Mai profitabil",
                                NL: "Winstgevender",
                            })}
                        </div>
                        <div className="text-zinc-500 text-sm">
                            {i18n({
                                EN: "Spend less time in uncertain consultations, utilise your resources more effectively and increase your order volume by up to 300%.",
                                SV: "Spendera mindre tid på osäkra konsultationer, utnyttja dina resurser mer effektivt och öka din ordervolym med upp till 300%.",
                                PTBR: "Gaste menos tempo em consultas incertas, utilize seus recursos de forma mais eficaz e aumente seu volume de pedidos em até 300%.",
                                PT: "Gaste menos tempo em reuniões incertas, utilize os seus recursos de forma mais eficaz e aumente o seu volume de encomendas até 300%.",
                                PL: "Poświęcaj mniej czasu na niepewne konsultacje, efektywniej wykorzystuj swoje zasoby i zwiększ liczbę zamówień nawet o 300%.",
                                ES: "Dedique menos tiempo a consultas inciertas, utilice sus recursos de forma más eficaz y aumente su volumen de pedidos hasta un 300%.",
                                CZ: "Strávíte méně času nejistými konzultacemi, efektivněji využijete své zdroje a zvýšíte objem objednávek až o 300 %.",
                                FR: "Consacrez moins de temps aux consultations incertaines, utilisez vos ressources plus efficacement et augmentez le nombre de commandes jusqu'à 300 %.",
                                IT: "Dedica meno tempo a chiamate e vendite incerte, utilizza le tue risorse in modo più efficace e aumenta il volume degli ordini fino al 300%.",
                                RO: "Petreceți mai puțin timp în consultări incerte, utilizați resursele mai eficient și creșteți volumul comenzilor cu până la 300%.",
                                NL: "Besteed minder tijd aan onzekere consultaties, benut uw middelen effectiever en verhoog uw ordervolume met tot wel 300%.",
                            })}
                        </div>
                    </div>
                </div>
            </Section>

            <Section relative={false} outerClassName="border-t">
                <div className="relative w-full">
                    <div className="absolute left-0 flex justify-end w-0">
                        <div className="grid grid-cols-12 w-[1000px] shrink-0 ">
                            {/* <Customers /> */}
                            {/* <Customers /> */}
                            {/* <Customers /> */}
                        </div>
                    </div>
                    <div>
                        {/* 56px */}
                        <div className="grid grid-cols-7 lg:grid-cols-12 grid-rows-11 h-[580px] md:h-[616px] w-full">
                            {/* <Customers /> */}
                            {/* <Customers />
                            <Customers /> */}

                            <div className="col-start-1 row-start-3 row-span-6 col-span-12 xl:col-span-8 py-16 transition-all p-6 flex justify-center bg-zinc-50 rounded-sm">
                                <div className="flex flex-col">
                                    <div className="font-bold text-3xl sm:text-4xl md:text-5xl text-left">
                                        {i18n({
                                            EN: "You're in good company.",
                                            SV: "Du är i gott sällskap.",
                                            PTBR: "Você está em boas mãos.",
                                            PT: "Está em boa companhia.",
                                            PL: "Jesteś w dobrym towarzystwie.",
                                            ES: "Estás en buena compañía.",
                                            CZ: "Jste v dobré společnosti.",
                                            FR: "Vous êtes en bonne compagnie.",
                                            IT: "Siete in buona compagnia.",
                                            RO: "Sunteți într-o companie bună.",
                                            NL: "U bent in goed gezelschap.",
                                        })}
                                    </div>
                                    <div className="font-normal text-2xl sm:text-3xl md:text-4xl text-zinc-500 mt-[6px]">
                                        {i18n({
                                            EN: "Over 500 customers already trust us.",
                                            SV: "Över 500 kunder litar redan på oss.",
                                            PTBR: "Mais de 500 clientes já confiam na gente.",
                                            PT: "Mais de 500 clientes já confiam em nós.",
                                            PL: "Zaufało nam już ponad 500 klientów.",
                                            ES: "Más de 500 clientes ya confían en nosotros.",
                                            CZ: "Důvěřuje nám již více než 500 zákazníků.",
                                            FR: "Plus de 500 clients nous font déjà confiance.",
                                            IT: "Oltre 500 clienti si fidano di noi.",
                                            RO: "Peste 500 de clienți au deja încredere în noi.",
                                            NL: "Meer dan 500 klanten vertrouwen ons al.",
                                        })}
                                    </div>
                                    <div className="mt-5 flex flex-wrap gap-2">
                                        <LinkAsButton
                                            to={locPath(locale, "/book-a-meeting/")}
                                            className="bg-black text-white whitespace-nowrap"
                                        >
                                            {i18n({
                                                EN: "Get a demo",
                                                SV: "Boka en demo",
                                                PTBR: "Agende uma demo",
                                                PT: "Pedir demonstração",
                                                PL: "Umów demo",
                                                ES: "Solicite una demo",
                                                CZ: "Získejte ukázku",
                                                FR: "Demander une démo",
                                                IT: "Richiedi una demo",
                                                RO: "Obțineți o demonstrație",
                                                NL: "Vraag een demo aan",
                                            })}
                                        </LinkAsButton>
                                        <LinkAsButton
                                            to={locPath(locale, "/about/us/")}
                                            className="bg-zinc-200 md:bg-transparent text-black whitespace-nowrap"
                                        >
                                            {i18n({
                                                EN: "Learn more about us",
                                                SV: "Läs mer",
                                                PTBR: "Conheça mais sobre a gente",
                                                PT: "Saiba mais sobre nós",
                                                PL: "Dowiedz się więcej o nas",
                                                ES: "Más información",
                                                CZ: "Zjistěte o nás více",
                                                FR: "Découvrez en plus sur nous",
                                                IT: "Per saperne di più su di noi",
                                                RO: "Aflați mai multe despre noi",
                                                NL: "Meer over ons",
                                            })}
                                        </LinkAsButton>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="absolute top-0 right-0 flex justify-start w-0">
                        <div className="grid grid-cols-12 w-[1000px] shrink-0 ">
                            {/* <Customers />
                            <Customers />
                            <Customers /> */}
                        </div>
                    </div>
                </div>
            </Section>
            <SectionVideoBackdrop outerClassName="" className="grow flex h-[800px] items-center">
                <div className="z-100 relative text-white  p-8 rounded-sm">
                    {subscribed && (
                        <>
                            <div className="font-bold text-5xl">
                                <Loc
                                    language={language}
                                    EN={
                                        <>
                                            Please confirm your email. <br />
                                            <span className="font-normal text-3xl mt-2">
                                                Only after you have confirmed your email, <br />
                                                we can add you to our newsletter.
                                            </span>
                                        </>
                                    }
                                    SV={
                                        <>
                                            Vänligen bekräfta din e-postadress. <br />
                                            <span className="font-normal text-3xl mt-2">
                                                Först efter att du har bekräftat din e-post, <br /> kan vi kan vi lägga
                                                till dig i vårt nyhetsbrev.
                                            </span>
                                        </>
                                    }
                                    PTBR={
                                        <>
                                            Por favor, confirme seu e-mail. <br />
                                            <span className="font-normal text-3xl mt-2">
                                                Somente depois que você confirmar seu e-mail, <br />
                                                poderemos adicioná-lo ao nosso boletim informativo.
                                            </span>
                                        </>
                                    }
                                    PT={
                                        <>
                                            Por favor confirme o seu email. <br />
                                            <span className="font-normal text-3xl mt-2">
                                                Só depois de ter confirmado o seu email, <br />
                                                podemos adicioná-lo à nossa newsletter.
                                            </span>
                                        </>
                                    }
                                    PL={
                                        <>
                                            Prosimy o potwierdzenie adresu e-mail. <br />
                                            <span className="font-normal text-3xl mt-2">
                                                Dopiero po potwierdzeniu adresu e-mail, <br />
                                                możemy dodać Cię do naszego newslettera.
                                            </span>
                                        </>
                                    } //
                                    ES={
                                        <>
                                            Confirme su dirección de correo electrónico. <br />
                                            <span className="font-normal text-3xl mt-2">
                                                Sólo después de que haya confirmado su correo electrónico, <br />
                                                podremos añadirle a nuestro boletín.
                                            </span>
                                        </>
                                    }
                                    CZ={
                                        <>
                                            Potvrďte prosím svůj e-mail. <br />
                                            <span className="font-normal text-3xl mt-2">
                                                Teprve poté, co potvrdíte svůj e-mail, <br />
                                                vás můžeme přidat do našeho zpravodaje.
                                            </span>
                                        </>
                                    }
                                    FR={
                                        <>
                                            Veuillez confirmer votre e-mail. <br />
                                            <span className="font-normal text-3xl mt-2">
                                                {" "}
                                                Tu ne peux pas te désinscrire. Ce n'est qu'après avoir confirmé ton
                                                e-mail que <br />
                                                nous pourrons t'inscrire à notre newsletter.
                                            </span>
                                        </>
                                    }
                                    IT={
                                        <>
                                            Si prega di confermare l'e-mail. <br />
                                            <span className="font-normal text-3xl mt-2">
                                                . Solo dopo aver confermato l'email, <br />
                                                potremo aggiungerla alla nostra newsletter.
                                            </span>
                                            .
                                        </>
                                    }
                                    RO={
                                        <>
                                            Vă rugăm să confirmați adresa dvs. de e-mail. <br />
                                            <span className="font-normal text-3xl mt-2">
                                                Numai după ce ați confirmat adresa dvs. de e-mail, <br />
                                                vă putem adăuga la buletinul nostru informativ.
                                            </span>
                                        </>
                                    }
                                    NL={
                                        <>
                                            Bevestig alstublieft uw e-mail. <br />
                                            <span className="font-normal text-3xl mt-2">
                                                Pas nadat u uw e-mail heeft bevestigd, <br />
                                                kunnen we u toevoegen aan onze nieuwsbrief.
                                            </span>
                                        </>
                                    }
                                />
                            </div>
                        </>
                    )}
                    {!subscribed && (
                        <>
                            <div className="font-bold text-5xl">
                                {i18n({
                                    EN: "Stay up to date.",
                                    SV: "Håll dig uppdaterad.",
                                    PTBR: "Mantenha-se atualizado.",
                                    PT: "Mantenha-se atualizado.",
                                    PL: "Bądź na bieżąco.",
                                    ES: "Manténgase al día.",
                                    CZ: "Zůstaňte v obraze.",
                                    FR: "Restez informé.",
                                    IT: "Rimanete aggiornati.",
                                    RO: "Rămâneți la curent.",
                                    NL: "Blijf op de hoogte.",
                                })}
                            </div>
                            <div className="text-sm  mt-[8px] mb-3">
                                {i18n({
                                    EN: "Subscribe to our newsletter and no longer miss any news.",
                                    SV: "Prenumerera på vårt nyhetsbrev och missa inga nyheter längre.",
                                    PTBR: "Assine nosso boletim informativo e não perca mais nenhuma novidade.",
                                    PT: "Subscreva a nossa newsletter e não perca mais nenhuma notícia.",
                                    PL: "Zapisz się do naszego newslettera i nie przegap żadnych nowości.",
                                    ES: "Suscríbase a nuestro boletín de noticias y no se pierda ninguna novedad.",
                                    CZ: "Přihlaste se k odběru našeho newsletteru a už vám neunikne žádná novinka.",
                                    FR: "Abonnez-vous à notre newsletter et ne manquez plus rien.",
                                    IT: "Iscrivetevi alla nostra newsletter per non perdere più nessuna novità.",
                                    RO: "Abonați-vă la newsletter-ul nostru și nu mai pierdeți nicio știre.",
                                    NL: "Neem een abonnement op onze nieuwsbrief en mis geen enkel nieuws meer.",
                                })}
                            </div>

                            <div className="flex sm:flex-row gap-2 sm:gap-0 flex-col mt-3 h-full">
                                <input
                                    value={name}
                                    onChange={(e) => {
                                        setName(e.target.value);
                                        setNewsletterError(false);
                                    }}
                                    className={`
                                        bg-white sm:rounded-r-none rounded-full overflow-hidden outline-black text-black outline-offset-1 px-4 h-[32px] border-r
                                        focus:z-10 ${newsletterError && "placeholder-red-500 text-red-500"}
                                    `}
                                    placeholder={i18n({
                                        EN: "Your name",
                                        SV: "Ditt namn",
                                        PTBR: "Seu nome",
                                        PT: "O seu nome",
                                        PL: "Imię i nazwisko",
                                        ES: "Su nombre",
                                        CZ: "Vaše jméno",
                                        FR: "Votre nom",
                                        IT: "Il tuo nome",
                                        RO: "Numele dvs.",
                                        NL: "Uw naam",
                                    })}
                                />
                                <input
                                    value={email}
                                    type="email"
                                    onChange={(e) => {
                                        setEmail(e.target.value);
                                        setNewsletterError(false);
                                    }}
                                    className={`
                                        border-r bg-white sm:rounded-l-none rounded-full  overflow-hidden text-black outline-black outline-offset-1 px-4 h-[32px]
                                        focus:z-10 ${newsletterError && "placeholder-red-500 text-red-500"}
                                    `}
                                    placeholder={i18n({
                                        EN: "Your email",
                                        SV: "Din e-postadress",
                                        PTBR: "Seu e-mail",
                                        PT: "O seu e-mail",
                                        PL: "Twój adres e-mail",
                                        ES: "Tu correo electrónico",
                                        CZ: "Váš e-mail",
                                        FR: "Votre email",
                                        IT: "Il tuo indirizzo e-mail",
                                        RO: "Adresa dvs. de e-mail",
                                        NL: "Uw e-mailadres",
                                    })}
                                />
                            </div>
                            {newsletterError && (
                                <span className="text-red-500 mt-4 text-[10px]">
                                    {i18n({
                                        EN: "Invalid input",
                                        SV: "Ogiltig inmatning",
                                        PTBR: "Entrada inválida",
                                        PT: "Entrada inválida",
                                        PL: "Nieprawidłowe dane wejściowe",
                                        ES: "Entrada no válida",
                                        CZ: "Neplatný vstup",
                                        FR: "Saisie non valide",
                                        IT: "Inserimento non valido",
                                        RO: "Introducere invalidă",
                                        NL: "Ongeldige invoer",
                                    })}
                                </span>
                            )}
                            <div className="flex gap-4 items-center">
                                <Button
                                    type="submit"
                                    className="bg-white mt-4 text-black"
                                    disabled={newsletterLoading}
                                    onClick={() => {
                                        if (email && /^\S+@\S+\.\S+$/.test(email) && name) {
                                            subscribe({ email: email, name: name });
                                            setNewsletterError(false);
                                            setSubscribed(true);
                                        } else {
                                            setNewsletterError(true);
                                        }
                                    }}
                                >
                                    {i18n({
                                        EN: "Sign up",
                                        SV: "Registrera dig",
                                        PTBR: "Registrar-se",
                                        PT: "Subscrever",
                                        PL: "Zarejestruj się",
                                        ES: "Inscríbete",
                                        CZ: "Zaregistrujte se",
                                        FR: "S'inscrire",
                                        IT: "Iscriviti",
                                        RO: "Înscrieți-vă",
                                        NL: "Aanmelden",
                                    })}
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            </SectionVideoBackdrop>
            <Footer locale={locale} {...props} />
        </>
    );
}
